import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDeleteModal } from "../../common/hooks/useDeleteModal";
import { AppSidebar } from "../../common/Sidebar";
import { CreateFolderButton } from "../../common/Sidebar/Components/SidebarButtons/CreateFolderButton";
import type { MenuItems } from "../../common/Sidebar/types";
import { ComparisonFolderNameChange } from "../../features/Comparison/Components/ComparisonFolderNameChange";
import { TransferComparisonObjectModal } from "../../features/Comparison/Components/TransferComparisonObjectModal";
import { deleteComparisonObjectAction } from "../../features/Comparison/redux/actions";
import { AppDeleteDialog } from "../../shared/components/AppDialog/AppDeleteDialog";
import { useComparisonPage } from "./hooks/use-comparison-page";
import { ObjectManagerModal } from "../../common/Components/Modals/components/ObjectManagerModal";
import { useAppModals } from "../../app/state/modalSlice/hook/use-app-modals";
import { SafeOutlet } from "../../common/Protectors/SafeOutlet";
import styles from "./styles.module.css";

export const ComparisonPage = () => {
  const {
    deleteInfo,
    isDeleteModalOpen,
    handleDelete,
    openDeleteModal,
    closeDeleteModal,
  } = useDeleteModal(deleteComparisonObjectAction);
  const {
    selectedComparisonObject,
    sidebarComparisonItems,
    isComparisonLoading,
    addComparisonObject,
    addFolder,
    handleSelectComparisonObject,
  } = useComparisonPage();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { handleCloseModal, handleOpenModal } = useAppModals();
  const [modalNameChangeOpen, setModalNameChangeOpen] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const handleModalOpen = (id: string) => {
    setSelectedId(id);
    setOpen(true);
  };
  const handleModalClose = () => {
    setOpen(false);
  };

  const handleOpenNameChangeModal = (id: string) => {
    setSelectedFolderId(id);
    setModalNameChangeOpen(true);
  };

  const handleCloseNameChangeModal = () => {
    setModalNameChangeOpen(false);
  };

  return (
    <div className={styles["page-layout"]}>
      <AppSidebar
        onAdd={addComparisonObject}
        menuItems={sidebarComparisonItems as MenuItems}
        defaultName={
          t("dialogs.default_name") || t("dialogs.default_folder_name")
        }
        selectedId={selectedComparisonObject?.id}
        onSelect={handleSelectComparisonObject}
        onDelete={(id, name) => openDeleteModal(id, name)}
        onOpenModal={(id) => handleModalOpen(id)}
        onOpenModalNameChange={(id) => handleOpenNameChangeModal(id)}
        service={"llm_compare"}
        isLoading={isComparisonLoading}
        isTransfer
        headerAdditionalContent={
          <>
            <CreateFolderButton
              handleCreateFolder={() =>
                handleOpenModal("object_manager", null, "create_folder")
              }
            />
          </>
        }
      />
      {selectedComparisonObject && <SafeOutlet />}
      <AppDeleteDialog
        dialogOpen={isDeleteModalOpen}
        deletingInfo={deleteInfo}
        handleDialogClose={closeDeleteModal}
        onDelete={handleDelete}
      />
      <TransferComparisonObjectModal
        open={open}
        childId={selectedId as string}
        closeModal={handleModalClose}
      />
      <ComparisonFolderNameChange
        open={modalNameChangeOpen}
        childId={selectedFolderId as string}
        closeModal={handleCloseNameChangeModal}
      />
      <ObjectManagerModal
        service={"llm_compare"}
        closeModal={handleCloseModal}
        defaultName={t("common.default_folder_name")}
        addFolder={addFolder}
      />
    </div>
  );
};
